/**
 * Environment gating utility (single source of truth for local / development / live).
 *
 * The deployed "development" site is a production build, so `process.env.NODE_ENV`
 * cannot tell it apart from live. Detection is therefore hostname-based, matching the
 * rest of the codebase (see `utils/subdomainDetection.ts`).
 *
 * Environments:
 *   - local:       hostname includes "localhost" or is "127.0.0.1"
 *   - development: not local AND hostname includes "development" (e.g. development.tweek.io)
 *   - live:        everything else
 *   - isNonProd:   local OR development (the common "hide on live" case)
 *
 * Usage examples:
 *
 * // 1. Booleans
 * // import { isNonProd, isLive } from "utils/environment";
 * // if (isNonProd()) { ...only runs on local + development... }
 *
 * // 2. Hide a nav item on live (conditional spread inside a nav array)
 * // ...(isNonProd()
 * //   ? [{ title: "Briefcase", link: "/mybriefcase", slug: "mybriefcase", activeIcon: "", inactiveIcon: "" }]
 * //   : []),
 *
 * // 3. Hide a button on live (show only on local + development)
 * //    a) Declarative wrapper:
 * //       import { EnvOnly } from "components/UI/EnvOnly/EnvOnly";
 * //       <EnvOnly envs={["local", "development"]}>
 * //         <button onClick={handleClick}>Experimental action</button>
 * //       </EnvOnly>
 * //    b) With the hook:
 * //       import { useEnv } from "hooks/useEnv";
 * //       const { isNonProd } = useEnv();
 * //       {isNonProd && <button onClick={handleClick}>Experimental action</button>}
 *
 * // 4. Route to a different page per environment
 * // import { pickByEnv } from "utils/environment";
 * // navigate(pickByEnv({ live: "/overview", development: "/overview-beta", local: "/overview-beta" }));
 *
 * // 5. Redirect away from a page entirely on live (e.g. lock it down)
 * // import { isLive } from "utils/environment";
 * // useEffect(() => {
 * //   if (isLive()) navigate("/overview", { replace: true });
 * // }, []);
 */

export type AppEnvironment = "local" | "development" | "live";

export const getAppEnvironment = (): AppEnvironment => {
  const hostname = window.location.hostname;
  if (hostname.includes("localhost") || hostname === "127.0.0.1") return "local";
  if (hostname.includes("development")) return "development";
  return "live";
};

export const isLocal = () => getAppEnvironment() === "local";
export const isDevelopment = () => getAppEnvironment() === "development";
export const isLive = () => getAppEnvironment() === "live";
export const isNonProd = () => !isLive();

/**
 * Select a value based on the current environment.
 *
 * `live` is required (the safe default). `local` and `development` are optional and
 * fall back: development -> local -> live when omitted. This lets callers express
 * "different on dev, same-as-live otherwise" concisely.
 *
 * // pickByEnv({ live: "/overview", development: "/overview-beta" })
 * //   -> "/overview-beta" on development and local, "/overview" on live
 */
export const pickByEnv = <T>(options: {
  local?: T;
  development?: T;
  live: T;
}): T => {
  const env = getAppEnvironment();
  if (env === "local") return options.local ?? options.live;
  if (env === "development")
    return options.development ?? options.local ?? options.live;
  return options.live;
};
